import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../page.css";
import "../../home.css";
import "../../trekking/kilimanjaro.css";
import "./serengeti.css";

const features = [
  {
    title: "Endless plains",
    text: "Serengeti means “endless plains” in Maa. Open grassland, kopjes, and river lines stretch farther than the eye can hold.",
  },
  {
    title: "Predator country",
    text: "Lions, leopards, cheetahs, and hyenas hunt across the same ground as huge herds of wildebeest, zebra, and gazelle.",
  },
  {
    title: "The great migration",
    text: "More than a million wildebeest and hundreds of thousands of zebra move with the rains. Timing shapes where you sleep and drive.",
  },
  {
    title: "Year round safari",
    text: "Even outside peak migration weeks, the central and western parks hold strong resident game and big cat sightings.",
  },
];

const parkStats = [
  { value: "14,763", label: "km² of wilderness" },
  { value: "1.5M+", label: "wildebeest migrate" },
  { value: "3,000+", label: "lions in the park" },
  { value: "UNESCO", label: "World Heritage Site" },
];

const regions = [
  {
    id: "seronera",
    name: "Central Serengeti, Seronera",
    days: "2 to 4 nights",
    style: "Classic game drives",
    lodging: "Lodge or tented camp",
    bestFor: "First time visitors and strong year round game",
    image:
      "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Wildlife on open Serengeti savanna plains",
    summary:
      "The heart of the park. Riverine woodland, open plains, and rocky kopjes give reliable sightings of lions, leopards, elephants, and mixed herds through most of the year.",
    body: "Seronera is where many private safaris begin. Distances between sightings are manageable, guides know the river systems well, and you can settle into a calm daily rhythm of morning and late afternoon drives. It is also the easiest area to combine with Ngorongoro or Tarangire.",
    highlights: [
      "Strong resident predator populations",
      "Kopje landscapes that photograph beautifully at golden hour",
      "Good base when migration herds are not nearby",
      "Simple logistics from Arusha or the crater highlands",
    ],
    itinerary: [
      { day: "Morning", text: "Early drive along river lines and open plains while cats are active." },
      { day: "Midday", text: "Return to camp for rest during the heat." },
      { day: "Afternoon", text: "Second drive toward kopjes, water points, and sunset light." },
      { day: "Optional", text: "Balloon safari over the plains at first light." },
    ],
    notes:
      "Choose Seronera when you want consistent game viewing without chasing the herds across the whole ecosystem.",
  },
  {
    id: "ndutu",
    name: "Southern plains and Ndutu",
    days: "2 to 4 nights",
    style: "Calving season focus",
    lodging: "Seasonal camp or lodge",
    bestFor: "December to March wildebeest calving",
    image:
      "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open grassland under wide East African sky",
    summary:
      "Short grass plains around Ndutu and the southern Serengeti fill with herds when the rains arrive. Predators follow closely, and days feel busy with calves and hunts.",
    body: "This is the calving window many guests ask for. The landscape is open, the herds can number in the tens of thousands on a single horizon, and cheetahs and lions work the same ground. Camps here are often seasonal, so we book early for peak weeks in February and early March.",
    highlights: [
      "Peak predator action around new calves",
      "Wide views and soft light on the short grass",
      "Easy pairing with Ngorongoro on the same loop",
      "Best when rains have greened the southern plains",
    ],
    itinerary: [
      { day: "Arrival", text: "Drive or fly in, afternoon game drive toward the herds." },
      { day: "Full days", text: "Long mornings on the plains, flexible routes based on herd movement." },
      { day: "Evenings", text: "Return before dark, dinner under canvas or at lodge." },
      { day: "Departure", text: "Continue to Seronera, the crater, or a flight connection." },
    ],
    notes:
      "Ndutu shines in the green season. Outside those months we usually shift you north or into central Serengeti instead.",
  },
  {
    id: "western-corridor",
    name: "Western Corridor",
    days: "2 to 3 nights",
    style: "River and woodland drives",
    lodging: "Lodge or tented camp",
    bestFor: "Grumeti crossings and quieter bush",
    image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Riverine woodland and open bush country",
    summary:
      "The Grumeti River corridor offers thicker bush, riverine forest, and dramatic crossing drama when the migration moves west, usually around May and June.",
    body: "Western camps sit closer to river systems where crocodiles wait and herds gather before crossing. Outside migration weeks the area still holds elephants, hippos, and resident cats, with fewer vehicles than Seronera on many days. It suits guests who want a slightly wilder feel without leaving the Serengeti ecosystem.",
    highlights: [
      "Grumeti river scenery and crossing potential in season",
      "Woodland birdlife and hippo pools",
      "Often quieter than central Serengeti",
      "Good link toward Lake Victoria side departures",
    ],
    itinerary: [
      { day: "Morning", text: "Drive river edges and woodland tracks." },
      { day: "Midday", text: "Rest at camp near the corridor." },
      { day: "Afternoon", text: "Follow herds or resident game along water." },
      { day: "In season", text: "Hold position near likely crossing points when herds mass." },
    ],
    notes:
      "Crossing days are never guaranteed. We plan time in the corridor and keep the schedule flexible around herd movement.",
  },
  {
    id: "northern",
    name: "Northern Serengeti, Kogatende and Lamai",
    days: "3 to 5 nights",
    style: "Mara river migration",
    lodging: "Mobile or permanent tented camp",
    bestFor: "July to October Mara River crossings",
    image:
      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "High open country under clear mountain light",
    summary:
      "The far north near the Mara River is the stage for famous crossing scenes when herds push toward Kenya and back again through the dry months.",
    body: "Northern Serengeti feels remote and open. Camps around Kogatende and Lamai put you close to river lookouts, and days can swing between quiet grazing plains and sudden crossing attempts. Fly in access is common. This is the region we recommend when your dates fall in the classic dry season migration window.",
    highlights: [
      "Mara River crossing potential in peak months",
      "Dramatic scenery and fewer lodge clusters than some southern camps",
      "Excellent for photography of herds and river action",
      "Often combined with a shorter Seronera stay",
    ],
    itinerary: [
      { day: "Fly in", text: "Light aircraft into a northern airstrip, transfer to camp." },
      { day: "River days", text: "Morning and afternoon drives along the Mara and nearby plains." },
      { day: "Flexible", text: "Guides track herd pressure and crossing attempts." },
      { day: "Exit", text: "Fly out toward Arusha, the crater, or Zanzibar connections." },
    ],
    notes:
      "Book northern camps early for July to October. We can also build a mixed itinerary if you want both central and northern nights.",
  },
];

const seasons = [
  {
    title: "Dec to March",
    text: "Southern short grass plains and Ndutu. Calving season, rich predator action, green landscapes after the rains.",
  },
  {
    title: "April to June",
    text: "Herds push through western and central routes. Lush scenery, some heavier rain, dramatic river corridor days.",
  },
  {
    title: "July to October",
    text: "Northern Serengeti and Mara River crossings. Dry, dusty, and excellent for classic migration photography.",
  },
  {
    title: "November",
    text: "Shoulder weeks as herds begin shifting south again. Good value and still strong resident game in the center.",
  },
];

const practical = [
  {
    title: "How long to stay",
    text: "Three nights is a solid minimum. Four to six nights let you cover more than one region without rushing every drive.",
  },
  {
    title: "How we travel",
    text: "Private 4x4 with a dedicated guide, or fly camp to camp when distances are long. Balloon mornings can be added in central areas.",
  },
  {
    title: "What we arrange",
    text: "Park fees, camps or lodges matched to season, airstrip transfers, and a clear day by day outline before you fly.",
  },
];

function Serengeti() {
  const { hash } = useLocation();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    document.title = "Serengeti National Park · Mirrorkhan Tours";
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [hash]);

  useEffect(() => {
    const node = statsRef.current;
    if (!node) return undefined;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setStatsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStatsVisible(true);
        observer.disconnect();
      },
      { threshold: 0.25 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="kili-page serengeti-page">
      <section className="kili-hero serengeti-hero">
        <div className="kili-hero__media" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1800&q=80"
            alt=""
          />
        </div>
        <div className="kili-hero__shade" aria-hidden="true" />
        <div className="container kili-hero__content">
          <div className="row">
            <div className="col-lg-8 col-xl-7 mk-hero-enter">
              <p className="eyebrow eyebrow--light mb-3">
                <Link
                  to="/safaris/northern-circuits"
                  className="text-decoration-none"
                >
                  Northern Circuit
                </Link>
              </p>
              <h1 className="mb-3">Serengeti National Park</h1>
              <p className="kili-hero__lede mb-4">
                Tanzania’s great plains safari. Private game drives across
                open grassland, river country, and migration ground, paced for
                your dates and how you like to spend a day in the bush.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a className="btn btn-mk-gold" href="#regions">
                  View regions
                </a>
                <Link className="btn btn-mk-ghost" to="/contact">
                  Ask about Serengeti
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={statsRef}
        className={
          statsVisible
            ? "serengeti-stats is-visible"
            : "serengeti-stats"
        }
        aria-label="Serengeti park facts"
      >
        <div className="container">
          <ul className="serengeti-stats__list list-unstyled mb-0">
            {parkStats.map((stat, index) => (
              <li
                className="serengeti-stats__item"
                key={stat.label}
                style={{ "--stat-delay": `${index * 120}ms` }}
              >
                <span className="serengeti-stats__value">{stat.value}</span>
                <span className="serengeti-stats__label">{stat.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal="true">
            <div className="col-lg-8">
              <p className="eyebrow">The park</p>
              <h2 className="mb-3">Why the Serengeti still leads every list</h2>
              <p className="page-lead mb-0">
                It is not only the migration. It is the scale of the land, the
                density of predators, and the way a quiet morning on the plains
                can turn in a single hour.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {features.map((item, index) => (
              <div
                className="col-md-6"
                key={item.title}
                data-reveal="true"
                data-reveal-delay={String((index % 2) * 100)}
              >
                <article className="kili-feature h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="regions" className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal="true">
            <div className="col-lg-8">
              <p className="eyebrow">Regions</p>
              <h2 className="mb-3">Choose the ground that fits your month</h2>
              <p className="page-lead mb-0">
                The Serengeti is one park with several moods. We match camp
                location to season so you spend more time watching wildlife and
                less time crossing empty distances.
              </p>
            </div>
          </div>

          <div className="kili-routes">
            {regions.map((region, index) => (
              <article
                className={
                  index % 2 === 1
                    ? "kili-route kili-route--flip"
                    : "kili-route"
                }
                key={region.id}
                id={region.id}
                data-reveal={index % 2 === 1 ? "right" : "left"}
              >
                <div className="kili-route__top">
                  <div className="kili-route__media">
                    <img src={region.image} alt={region.imageAlt} />
                  </div>
                  <div className="kili-route__intro">
                    <p className="kili-route__meta mb-2">
                      {region.days}
                      <span aria-hidden="true"> · </span>
                      {region.style}
                    </p>
                    <h3 className="h3 mb-3">{region.name}</h3>
                    <p className="kili-route__summary">{region.summary}</p>
                    <p className="kili-route__body-copy mb-0">{region.body}</p>
                  </div>
                </div>

                <div className="kili-route__panel">
                  <dl className="kili-route__facts">
                    <div>
                      <dt>Best for</dt>
                      <dd>{region.bestFor}</dd>
                    </div>
                    <div>
                      <dt>Stay</dt>
                      <dd>{region.days}</dd>
                    </div>
                    <div>
                      <dt>Lodging</dt>
                      <dd>{region.lodging}</dd>
                    </div>
                    <div>
                      <dt>Style</dt>
                      <dd>{region.style}</dd>
                    </div>
                  </dl>

                  <div className="row g-4">
                    <div className="col-lg-5">
                      <h4 className="kili-route__subhead">On the plains</h4>
                      <ul className="trek-points mb-0">
                        {region.highlights.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-lg-7">
                      <h4 className="kili-route__subhead">Sample day rhythm</h4>
                      <ol className="kili-itinerary list-unstyled mb-0">
                        {region.itinerary.map((step) => (
                          <li key={step.day}>
                            <strong>{step.day}</strong>
                            <span>{step.text}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>

                  <p className="kili-route__note mb-3">{region.notes}</p>
                  <Link className="text-link" to="/contact">
                    Ask about {region.name}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal="true">
            <div className="col-lg-8">
              <p className="eyebrow">Seasons</p>
              <h2 className="mb-3">Where the herds tend to be</h2>
              <p className="page-lead mb-0">
                Migration timing shifts with rain. These windows are a planning
                guide, not a promise, and we adjust camps when the herds move
                early or late.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {seasons.map((item, index) => (
              <div
                className="col-md-6 col-lg-3"
                key={item.title}
                data-reveal="true"
                data-reveal-delay={String(index * 80)}
              >
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal="true">
            <div className="col-lg-8">
              <p className="eyebrow">Before you go</p>
              <h2 className="mb-0">Practical notes from the desk</h2>
            </div>
          </div>

          <div className="row g-4">
            {practical.map((item, index) => (
              <div
                className="col-md-4"
                key={item.title}
                data-reveal="true"
                data-reveal-delay={String(index * 90)}
              >
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-close" data-reveal="fade">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <p className="eyebrow eyebrow--light">Plan your safari</p>
              <h2 className="mb-3">Tell us your month and how many nights you want in the Serengeti.</h2>
              <p className="mb-0 home-close__copy">
                We reply with region advice, camp options, and a clear price for
                your group size, with or without a Zanzibar finish.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link className="btn btn-mk-gold" to="/contact">
                Contact the desk
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Serengeti;
